import { CircularProgress } from '@material-ui/core';
import { TimeSpan } from '@moderepo/mode-app-components';
import React, { useMemo } from 'react';
import { EntityUIComponentCollection, FetchEntityTimeSpanDataParams, filterMetricsDefinitionsByMetricsExclusionCustomSettings,
    isFetchEntityTimeRangeDataParams, isGridCustomSettings, isMetricsDefinitionAndMetricsExclusionCustomSettings,
    MetricsDefinitionsAndMetricsExclusionCustomSettings, useSelectTranslatedEntityClasses, GridCustomSettings } from '..';
import { ExtGrid } from '../..';
import { BasicXYChart, BASIC_XY_CHART_TYPES } from '../../../utils/constants';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingStringValue } from '../../../utils/utils';
import { EntityUIComponentProps } from '../EntityDetailsLayouts';
import { getKPIMetricDefinitions } from '../utils';
import { KPIMetricDefinitionViewContainer } from './KPIMetricDefinitionViewContainer';



/**
 * Custom settings for EntityKPIMetricsViewUIComponent. These settings can be configured from Entity Class's uiSettings
 */
export interface EntityKPIMetricsViewUIComponentCustomSettings extends MetricsDefinitionsAndMetricsExclusionCustomSettings, GridCustomSettings {

    // To specify a different chart type to use for ALL metrics shown in this component instead of using the default chart type, AREA.
    readonly chartType?: BasicXYChart | undefined;

    // To specify a different chart type for a specific metric/metrics. This allow the user to choose a different chart type
    // for any metric instead of using the default chart type, AREA. Note that this is a map of metricName => chartType instead
    // 1 attribute because sometime we want to use a different chart type just for 1 or some metrics but not all metrics.
    readonly chartTypePerMetric?: {
        readonly [metricName: string]: BasicXYChart | undefined;
    } | undefined;
}


/**
 * type guard function to check if an object is a type of EntityKPIMetricsViewUIComponentCustomSettings
 */
const isEntityKPIMetricsViewUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityKPIMetricsViewUIComponentCustomSettings => {
    const settings = obj as EntityKPIMetricsViewUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.chartType, false, false, BASIC_XY_CHART_TYPES)) {
        return errorLogger?.logError({
            message: `"chartType" must be undefined or one of the following [${
                BASIC_XY_CHART_TYPES.map((chartTpe) => {
                    return `"${chartTpe}"`;
                }).join(', ')}].`,
            value: settings.chartType,
            path : [...configPath, 'chartType'],
        }) ?? false;
    }

    if (settings.chartTypePerMetric !== undefined) {
        if (!isObjectLiteral(settings.chartTypePerMetric)) {
            return errorLogger?.logError({
                message: '"chartTypePerMetric" must be undefined or an object.',
                value  : settings.chartTypePerMetric,
                path   : [...configPath, 'chartTypePerMetric'],
            }) ?? false;
        }

        // If one of the values in chartTypePerMetric is not a valid type then return false
        if (!Object.entries(settings.chartTypePerMetric).every(([metricName, chartType]) => {
            if (!isValidSettingStringValue(chartType, true, false, BASIC_XY_CHART_TYPES)) {
                return errorLogger?.logError({
                    message: `All values in "chartTypePerMetric" object must be one of the following [${
                        BASIC_XY_CHART_TYPES.map((chartTpe) => {
                            return `"${chartTpe}"`;
                        }).join(', ')}].`,
                    value: chartType,
                    path : [...configPath, 'chartTypePerMetric', metricName],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // Because this component's settings interface extends MetricsDefinitionAndMetricsExclusionCustomSettings, we can call
    // "isMetricsDefinitionAndMetricsExclusionCustomSettings" to validate it
    if (!isMetricsDefinitionAndMetricsExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // Because this CustomSettings interface extends GridCustomSettings interface, we can call the "isGridCustomSettings" to validate the
    // gridSizes settings
    if (!isGridCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};



export interface EntityKPIMetricsViewUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityKPIMetricsViewUIComponentCustomSettings | undefined;
}


/**
 * This component display metrics for 1 entity but ONLY KPI metrics
 */
export const EntityKPIMetricsViewUIComponent: React.FC<EntityKPIMetricsViewUIComponentProps> = (props: EntityKPIMetricsViewUIComponentProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    // For now, this component will only support FetchEntityTimeSpanDataParams. So if the fetchDataParams is another type,
    // we will try to create FetchEntityTimeSpanDataParams using whatever value we have from the fetchDataParams.
    // This component is used for displaying KPI metrics so we might not need to support other fetchDataParams
    const fetchDataParams = useMemo((): FetchEntityTimeSpanDataParams => {
        if (isFetchEntityTimeRangeDataParams(props.fetchDataParams)) {
            return {
                timestamps: [props.fetchDataParams.startTime],
                timeSpan  : TimeSpan.DAY,
            };
        }
        return props.fetchDataParams ? props.fetchDataParams : {
            timeSpan  : TimeSpan.DAY,
            timestamps: [Date.now()],
        };
    }, [props.fetchDataParams]);


    // Find the entityClass object that the selectedEntity belonged to
    const selectedEntityClass = useMemo(() => {
        if (selectedEntity && entityClasses) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);



    /**
     * Go through the metricDefinition's numericMetrics array and get the list of KPI numeric metrics, the numeric metrics that has 'evaluator'
     * attribute.
     */
    const kpiMetricsDefinitions = useMemo(() => {
        const kpiMetricsDef = getKPIMetricDefinitions(selectedEntityClass);

        // filter out metrics definitions based on the customSettings' excludeMetricsDefinitionIds
        const filteredMetricsDef = filterMetricsDefinitionsByMetricsExclusionCustomSettings(
            kpiMetricsDef ?? [], props.customSettings,
        );

        return filteredMetricsDef;
    }, [props.customSettings, selectedEntityClass]);


    return (
        <>
            {project && selectedEntity && selectedEntityClass ? (
                <ExtGrid
                    className={props.className}
                    style={props.style}
                    container
                    xs_spacing={1}
                    md_spacing={2}
                    lg_spacing={3}
                >

                    {kpiMetricsDefinitions?.map((metricDef) => {
                        return (
                            <KPIMetricDefinitionViewContainer
                                key={metricDef.metricsDefinitionId}
                                projectId={props.projectId}
                                entityId={props.entityId}
                                metricDefinitionId={metricDef.metricsDefinitionId}
                                metricsExclusionSettings={props.customSettings}
                                timeSpan={fetchDataParams.timeSpan}
                                timestamps={fetchDataParams.timestamps}
                                chartType={props.customSettings?.chartType}
                                chartTypePerMetric={props.customSettings?.chartTypePerMetric}
                                gridSizes={props.customSettings?.gridSizes}
                            />
                        );
                    })}
                </ExtGrid>
            ) : (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('EntityKPIMetricsViewUIComponent', EntityKPIMetricsViewUIComponent);


// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_KPI_Metrics_Comp',
    {
        componentFunc          : EntityKPIMetricsViewUIComponent,
        customSettingsValidator: isEntityKPIMetricsViewUIComponentCustomSettings,
    },
);
